// caiet UC91-07 / UC91-10 — the two things the user does not choose and the one
// they do. Bucketing follows the interval (UC91-07) and is the server's answer, so
// there is no granularity control and no copy of the rule here; the geographic
// aggregation level is a choice (UC91-10), and which levels are on offer follows
// the role.
import {
  addCalendarDays,
  businessDate,
  isCalendarDay,
  isObserverRole,
  type Role,
} from '@simsm/shared-types';
import type { Dimension } from './useStockTimeseries';

export type AggregationLevel = 'none' | 'locality' | 'region' | 'national';

// UC91-10 gives the geographic levels to the System Administrator and the
// Observer, locality to the administrator alone. Reporting roles read their own
// Registrar's movements, so they only ever see the unaggregated list.
// The Observer also reads unaggregated, IMSP Registrars only (UC91-01, UC91-04,
// UC91-17, CF-23): the server keeps the rows to IMSP and to the UC91-04 columns.
// The wireframe offers it the same three levels, unaggregated first
// (docs/shape-ops-observer-imsp-detail.md §2 D4).
type OfferedLevels = [AggregationLevel, ...AggregationLevel[]];

const ADMIN_LEVELS: OfferedLevels = ['none', 'locality', 'region', 'national'];
const OBSERVER_LEVELS: OfferedLevels = ['none', 'region', 'national'];
const REPORTER_LEVELS: OfferedLevels = ['none'];

export function levelsFor(role: Role | undefined): OfferedLevels {
  if (role === 'SYSTEM_ADMIN') return ADMIN_LEVELS;
  if (role && isObserverRole(role)) return OBSERVER_LEVELS;
  return REPORTER_LEVELS;
}

// A level the role may not select never survives the URL: an Observer who
// bookmarks `level=none` lands on their own first level, not on a forbidden one.
export function resolveLevel(raw: string | null, role: Role | undefined): AggregationLevel {
  const [fallback, ...rest] = levelsFor(role);
  if (raw === fallback) return fallback;
  return rest.find((level) => level === raw) ?? fallback;
}

// The transport dimension each level is read through. `site` is the one that reads
// the base hypertable, which is where lot, reason and site live, so it serves the
// unaggregated list; `region` and `locality` return one series per place, which
// is what the Region and Locality columns of UC91-04 list; `national` is the
// plain total. A level never reads as another: the server refuses a dimension it
// does not know, so a mapping that was wrong would show as an error, not as the
// national figure under a regional heading.
const DIMENSION_BY_LEVEL: Record<AggregationLevel, Dimension> = {
  none: 'site',
  locality: 'locality',
  region: 'region',
  national: 'national',
};

export function dimensionFor(level: AggregationLevel): Dimension {
  return DIMENSION_BY_LEVEL[level];
}

// caiet UC91-06: a period is two calendar days, the first not after the second
// (the wireframe's `perioadaOk`, Stocuri.tsx). Anything else is not asked for: the
// server would refuse it, and the screen says what is wrong where the dates are
// (TC-UC91-06-03) instead of reporting a failed load.
export function isValidPeriod(from: string, to: string): boolean {
  return isCalendarDay(from) && isCalendarDay(to) && from <= to;
}

// UC91-06: the default window is [-30, +29] days around today. Today is the
// date in Europe/Chisinau at `now`, whatever zone the browser is set to (ARCH-15;
// docs/shape-ops-business-date.md D2).
export function defaultRange(now = new Date()): { from: string; to: string } {
  return { from: isoDayFromToday(-30, now), to: isoDayFromToday(29, now) };
}

export function parseCsv(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

// caiet UC91-06 / wireframe `Stocuri.tsx:32-40` — the period is chosen from named
// presets, not typed twice. The application offered two bare ISO date inputs and
// nothing else, so "the last six months" was two calendar calculations the
// reader had to do themselves. Custom stays available; it is the seventh entry,
// exactly as the wireframe draws it.
export const PERIOD_PRESETS = [
  { id: 'default', fromDays: -30, toDays: 29 },
  { id: 'last7', fromDays: -7, toDays: 0 },
  { id: 'last90', fromDays: -90, toDays: 0 },
  { id: 'last6m', fromDays: -182, toDays: 0 },
  { id: 'last3y', fromDays: -3 * 365, toDays: 0 },
  { id: 'last5y', fromDays: -5 * 365, toDays: 0 },
] as const;

export type PeriodPresetId = (typeof PERIOD_PRESETS)[number]['id'] | 'custom';

export function isoDayFromToday(offsetDays: number, now = new Date()): string {
  return addCalendarDays(businessDate(now), offsetDays);
}

export function rangeForPreset(
  id: PeriodPresetId,
  today = new Date(),
): { from: string; to: string } | null {
  const preset = PERIOD_PRESETS.find((p) => p.id === id);
  if (!preset) return null;
  return {
    from: isoDayFromToday(preset.fromDays, today),
    to: isoDayFromToday(preset.toDays, today),
  };
}

// Which preset a range *is*, so the control shows what the address says rather
// than resetting to a default the reader did not choose.
export function presetForRange(from: string, to: string, today = new Date()): PeriodPresetId {
  const match = PERIOD_PRESETS.find((preset) => {
    const range = rangeForPreset(preset.id, today);
    return range !== null && range.from === from && range.to === to;
  });
  return match ? match.id : 'custom';
}
